/**
 * Post Edit Setup - Initialization and Dependency Injection
 * Extends PostCreateSetup pattern for edit mode functionality
 * Handles form initialization with media uploads and SNS link management
 */

import { PostEditHandler } from "./post-edit-handler";
import type { PostEditElements } from "./post-edit-handler";
import { postEditForm } from "./post-edit-form";
import { renderSnsLinkInputs } from "./post-edit-render";
import { ImageSetup } from "../../util/media/image/image-setup";
import { VideoSetup } from "../../util/media/video/video-setup";
import { phpPostData } from "../../core/php-data-access";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { PostSharedTabsSetup } from "../tabs/post-tabs-setup";
import { PostSharedUtils } from "../util/post-shared-utils";
import { SNSPostSetup } from "../../sns/post/sns-post-setup";
import { Roles } from "../../constants/roles";
import { PostProductLinker } from "../linker/product-linker-setup";
import { SearchMenuSetup } from "../../search/menu/search-menu-setup";
import { BackNumberToggleHandler } from "../shared/back-number-toggle-handler";
import { PostContentCharCounterHandler } from "../shared/post-content-char-counter-handler";
import { ModalApiService } from "../../modal/modal-api";

interface OriginalEditData {
    post_title?: string;
    content?: string;
    attached_images?: string[];
    attached_videos?: string[];
    sns_links?: string[];
    post_product_id?: number | null;
}

interface PostEditInstance {
    handler: PostEditHandler;
    elements: PostEditElements;
}

class PostEditSetup {
    public static _instances = new Map<string, PostEditInstance>();

    private static resolveScopedFormContainer(postKey: string, postContainer?: HTMLElement | null): HTMLElement | null {
        const scopedForm = postContainer ? postEditForm.getEditForm(postContainer) : null;
        if (scopedForm instanceof HTMLElement) {
            return scopedForm;
        }

        return postEditForm.getFormContainer(postKey);
    }

    private static toPostId(rawPostId: string): number | null {
        const normalizedPostId = Number(rawPostId);
        return Number.isInteger(normalizedPostId) && normalizedPostId > 0 ? normalizedPostId : null;
    }

    public static async initEdit(postId: number, postContainer: HTMLElement): Promise<PostEditInstance | null> {
        if (!postContainer) {
            console.error("[PostEditSetup] Post container required");
            return null;
        }

        const postKey = String(postId);
        PostEditSetup.cleanup(postKey);

        const boardId = postContainer.dataset.boardId || "";
        if (!boardId) {
            console.error("[PostEditSetup] boardId not found on postContainer");
            return null;
        }

        const editFormEl = await PostEditSetup.fetchAndInjectEditForm(postId, boardId, postContainer);

        if (!(editFormEl instanceof HTMLElement)) {
            console.error("[PostEditSetup] Failed to load edit form");
            return null;
        }

        PostEditSetup.toggleEditUI(postContainer, true);

        const modalBody = postContainer.closest(".element-modal-body");
        if (modalBody instanceof HTMLElement) {
            modalBody.scrollTop = 0;
        }

        // Dispatch app:content:inserted so initRegistry scans the injected form.
        // Required for WYSIWYG (Quill) initialization — the textarea is hidden by CSS
        // until WysiwygSetup.init() mounts the editor div.
        try {
            document.dispatchEvent(new CustomEvent("app:content:inserted", { detail: { root: editFormEl } }));
        } catch (_e) {
            void _e;
        }

        const instance = await PostEditSetup.init(postKey, editFormEl);

        const contentTextarea = postEditForm.getContentTextarea(postContainer);
        if (contentTextarea instanceof HTMLTextAreaElement) {
            PostContentCharCounterHandler.updateCounter(contentTextarea);
        }

        return instance;
    }

    private static async fetchAndInjectEditForm(
        postId: number,
        boardId: string,
        postContainer: HTMLElement,
    ): Promise<HTMLElement | null> {
        try {
            // XHR: GET /posts/{boardId}/posts/{postId}/edit — edit form HTML fragment (web route, ajax-only)
            const doc = await ModalApiService.fetchPostEditFormDocument(postId, boardId);
            if (!(doc instanceof Document)) return null;
            const formEl = postEditForm.getEditForm(doc);
            if (!(formEl instanceof HTMLElement)) return null;
            postContainer.insertAdjacentElement("afterbegin", formEl);
            return formEl;
        } catch (error) {
            console.error("[PostEditSetup] fetchAndInjectEditForm failed", error);
            return null;
        }
    }

    public static saveOriginalData(postKey: string, postContainer: HTMLElement): OriginalEditData {
        const titleElement = postEditForm.getTitleElement(postContainer);
        const contentElement = postEditForm.getPostContent(postContainer);
        const normalizedPostId = PostEditSetup.toPostId(postKey);
        const postData = normalizedPostId ? phpPostData(normalizedPostId) : null;

        let postTitleText = titleElement?.textContent?.trim?.() || "";

        if (!postTitleText) {
            const rawPostTitle = postData?.post_title;

            if (typeof rawPostTitle === "string") {
                postTitleText = rawPostTitle.trim();
            }
        }

        let snsLinks = Array.isArray(postData?.sns_links)
            ? SNSLinksUtils.normalizeSnsLinks(postData.sns_links.map((value) => String(value || "")))
            : [];

        if (snsLinks.length === 0) {
            try {
                const snsLinksDataEl = postEditForm.getPostSnsLinksData(postContainer);
                const raw = snsLinksDataEl?.getAttribute("data-sns-links") || "";
                if (raw) {
                    snsLinks = SNSLinksUtils.parseSnsLinksDataAttribute(raw);
                }
            } catch (_error) {
                snsLinks = [];
            }
        }

        const imgs = postEditForm.getExistingImageElements(postContainer);
        const attachedImages = imgs
            .map((img) => (img.getAttribute("src") || "").split("/").pop() || "")
            .filter((value) => value.trim() !== "");

        const videos = postEditForm.getExistingVideoSources(postContainer);
        const attachedVideos = videos
            .map((source) => (source.getAttribute("src") || "").split("/").pop() || "")
            .filter((value) => value.trim() !== "");

        const productIdInput = postEditForm.getLinkedProductIdInput(postContainer);
        const postProductId = productIdInput ? PostEditSetup.toPostId(String(productIdInput.value || "").trim()) : null;

        const rawContent = typeof postData?.content === "string" ? postData.content : null;
        const contentHtml = rawContent ?? (contentElement instanceof HTMLElement ? contentElement.innerHTML : "");

        const originalData: OriginalEditData = {
            post_title: postTitleText,
            content: contentHtml,
            attached_images: Array.from(new Set(attachedImages)),
            attached_videos: Array.from(new Set(attachedVideos)),
            sns_links: snsLinks,
            post_product_id: postProductId,
        };

        postContainer.dataset.originalEditData = JSON.stringify(originalData);

        return originalData;
    }

    public static copyOriginalDataToEditForm(
        postKey: string,
        postContainer: HTMLElement,
        originalData: OriginalEditData,
    ): void {
        void postKey;

        try {
            const editForm = postEditForm.getEditForm(postContainer);
            if (!editForm) return;

            const titleInput = postEditForm.getTitleInput(editForm);
            const contentTextarea = postEditForm.getContentTextarea(editForm);

            if (titleInput && titleInput.type !== "hidden" && typeof originalData?.post_title === "string") {
                const nextPostTitle = originalData.post_title.trim();
                if (nextPostTitle !== "") {
                    titleInput.value = nextPostTitle;
                }
            }

            if (contentTextarea && typeof originalData?.content === "string") {
                contentTextarea.value = originalData.content;

                const previousEditor = contentTextarea.previousElementSibling;
                const editorCandidate =
                    previousEditor instanceof HTMLElement && previousEditor.dataset.role === Roles.WYSIWYG_EDITOR
                        ? previousEditor
                        : postEditForm.getWysiwygEditor(contentTextarea.parentElement || editForm);
                const editorDiv = editorCandidate instanceof HTMLElement ? editorCandidate : null;

                const quill = editorDiv ? Reflect.get(editorDiv, "__quill") : null;
                const clipboard = quill && typeof quill === "object" ? Reflect.get(quill, "clipboard") : null;
                const setContents = quill && typeof quill === "object" ? Reflect.get(quill, "setContents") : null;

                if (clipboard && typeof clipboard === "object" && typeof setContents === "function") {
                    try {
                        const convert = Reflect.get(clipboard, "convert");
                        if (typeof convert === "function") {
                            const delta = convert(contentTextarea.value || "");
                            setContents.call(quill, delta, "api");
                        }
                    } catch (_error) {
                        void _error;
                    }
                }
            }

            const snsLinksList = postEditForm.getSnsLinksList(editForm);
            if (snsLinksList && Array.isArray(originalData?.sns_links)) {
                const normalized = SNSLinksUtils.normalizeSnsLinks(originalData.sns_links);
                renderSnsLinkInputs(snsLinksList, normalized);
            }
        } catch (_error) {
            void _error;
        }
    }

    public static toggleEditUI(postContainer: HTMLElement, showEdit: boolean): void {
        const editForm = postEditForm.getEditForm(postContainer);
        const postContent = postEditForm.getPostContent(postContainer);
        const postHeader = postEditForm.getPostHeader(postContainer);
        const attachments = postEditForm.getAttachmentsSection(postContainer);
        const mediaSection = postEditForm.getMediaSection(postContainer);
        // Kept in the DOM below the edit form otherwise, which stretches the page far past
        // the form itself and makes the WYSIWYG toolbar's sticky range end long before the
        // user is done scrolling (looks like sticky "doesn't work" in edit mode; it does —
        // there's just leftover read-view content still occupying scroll height below it).
        const userProfileSection = postEditForm.getUserProfileSection(postContainer);
        const commentsSection = postEditForm.getCommentsSection(postContainer);
        const editingClass = "post-detail--editing";

        if (!editForm) {
            console.error("[PostEditSetup] Edit form not found");
            return;
        }

        if (showEdit) {
            postContainer.classList.add(editingClass);
            editForm.style.display = "block";
            if (postContent) postContent.style.display = "none";
            if (postHeader) postHeader.style.display = "none";

            if (attachments) {
                attachments.dataset.originalDisplay = attachments.style.display || "";
                attachments.style.display = "none";
            }

            if (mediaSection instanceof HTMLElement) {
                mediaSection.dataset.originalDisplay = mediaSection.style.display || "";
                mediaSection.style.display = "none";
            }

            if (userProfileSection instanceof HTMLElement) {
                userProfileSection.dataset.originalDisplay = userProfileSection.style.display || "";
                userProfileSection.style.display = "none";
            }

            if (commentsSection instanceof HTMLElement) {
                commentsSection.dataset.originalDisplay = commentsSection.style.display || "";
                commentsSection.style.display = "none";
            }
            return;
        }

        postContainer.classList.remove(editingClass);
        if (postContent) postContent.style.display = "block";
        if (postHeader) postHeader.style.display = "block";

        if (attachments) {
            attachments.style.display = attachments.dataset.originalDisplay || "";
            delete attachments.dataset.originalDisplay;
        }

        if (mediaSection instanceof HTMLElement) {
            mediaSection.style.display = mediaSection.dataset.originalDisplay || "";
            delete mediaSection.dataset.originalDisplay;
        }

        if (userProfileSection instanceof HTMLElement) {
            userProfileSection.style.display = userProfileSection.dataset.originalDisplay || "";
            delete userProfileSection.dataset.originalDisplay;
        }

        if (commentsSection instanceof HTMLElement) {
            commentsSection.style.display = commentsSection.dataset.originalDisplay || "";
            delete commentsSection.dataset.originalDisplay;
        }

        // Destroy the fetched edit form DOM completely so next edit opens fresh.
        editForm.remove();
    }

    public static async init(
        postKey: string,
        formContainerOverride: HTMLElement | null = null,
    ): Promise<PostEditInstance | null> {
        const cachedInstance = PostEditSetup._instances.get(postKey) || null;
        if (
            cachedInstance &&
            (!formContainerOverride || cachedInstance.elements.formContainer === formContainerOverride)
        ) {
            return cachedInstance;
        }

        const elements = PostEditSetup.initializeElements(postKey, formContainerOverride);
        if (!elements.formContainer) {
            console.warn("[Post Edit Setup] Form container not found for post:", postKey);
            return null;
        }

        const formContainer = elements.formContainer instanceof HTMLElement ? elements.formContainer : null;
        if (!formContainer) {
            console.warn("[Post Edit Setup] Form container is not an HTMLElement for post:", postKey);
            return null;
        }

        await PostSharedUtils.waitForVisible(formContainer);

        BackNumberToggleHandler.init(formContainer);

        SearchMenuSetup.init(formContainer);
        PostProductLinker.init(formContainer);

        const handler = new PostEditHandler();
        handler.elements = elements;

        SNSPostSetup.registerListeners({
            postKey,
            rootEl: formContainer,
            handler,
        });

        PostEditSetup.setupTabSwitching(postKey, elements, handler);
        await PostEditSetup.initializeMediaManagers(postKey, elements);
        PostEditSetup.populateSnsInputs(elements.snsLinksList || null, postKey);

        SNSPostSetup.wireSnsLinksManager({
            postKey,
            formRoot: formContainer,
            handler,
            elements,
            initialSyncDelayMs: 100,
        });

        PostContentCharCounterHandler.init(formContainer);

        const instance: PostEditInstance = { handler, elements };
        PostEditSetup._instances.set(postKey, instance);

        return instance;
    }

    public static populateSnsInputs(snsLinksList: HTMLElement | null, postKey: string): void {
        if (!snsLinksList) {
            console.warn("[Post Edit Setup] SNS links list not found");
            return;
        }

        const normalizedPostId = PostEditSetup.toPostId(postKey);
        const postData = normalizedPostId
            ? ((phpPostData(normalizedPostId) as { sns_links?: string[] | string } | null | undefined) ?? null)
            : null;

        let snsLinks: string[] | string | null | undefined = postData?.sns_links;

        if (!Array.isArray(snsLinks) || snsLinks.length === 0) {
            snsLinks = PostEditSetup.getSnsLinksFromDom(snsLinksList);
        }

        if (!Array.isArray(snsLinks) || snsLinks.length === 0) {
            return;
        }

        const normalizedLinks = SNSLinksUtils.normalizeSnsLinks(snsLinks);

        const form = postEditForm.getClosestForm(snsLinksList);
        if (form) {
            postEditForm.getHiddenSnsLinkInputs(form).forEach((element) => element.remove());
        }

        renderSnsLinkInputs(snsLinksList, normalizedLinks);
    }

    public static getSnsLinksFromDom(snsLinksList: HTMLElement): string[] {
        try {
            const postRoot = postEditForm.getPostDetailRoot(snsLinksList) || document;

            const snsLinksDataEl = postEditForm.getPostSnsLinksData(postRoot);
            if (!snsLinksDataEl) {
                return [];
            }

            const raw = snsLinksDataEl.getAttribute("data-sns-links");
            if (!raw) {
                return [];
            }

            return SNSLinksUtils.parseSnsLinksDataAttribute(raw);
        } catch (error) {
            console.warn("[Post Edit Setup] Failed to read sns_links from DOM:", error);
            return [];
        }
    }

    public static initializeElements(
        postKey: string,
        formContainerOverride: HTMLElement | null = null,
    ): PostEditElements {
        const formContainer = formContainerOverride || postEditForm.getFormContainer(postKey);

        if (!(formContainer instanceof HTMLElement)) {
            return { formContainer: null };
        }

        const formCandidate = postEditForm.getFormInner(formContainer);
        const formEl = formCandidate instanceof HTMLElement ? formCandidate : formContainer;

        return {
            formContainer,
            form: formEl,
            snsLinksList: postEditForm.getSnsLinksList(formContainer),
            snsAddBtn: postEditForm.getSnsAddBtn(formContainer),
            youtubeSection: postEditForm.getYoutubeSection(formContainer),
            youtubeContainer: postEditForm.getYoutubeContainer(formContainer),
            snsContainer: postEditForm.getSnsContainer(formContainer),
            ogpContainer: postEditForm.getOgpContainer(formContainer),
            contentTextarea: postEditForm.getContentTextarea(formContainer),
            previewRoot: postEditForm.getPreviewRoot(formContainer),
            previewRichText: postEditForm.getPreviewRichText(formContainer),
            snsTabContainer: null,
            ogpTabContainer: postEditForm.getOgpPreviewTab(formContainer),
            imageManager: postEditForm.getImageManager(formContainer, postKey),
            videoManager: postEditForm.getVideoManager(formContainer, postKey),
        };
    }

    public static setupTabSwitching(postKey: string, elements: PostEditElements, handler: PostEditHandler): void {
        void postKey;

        const rootEl = elements.form instanceof HTMLElement ? elements.form : elements.formContainer;
        if (!(rootEl instanceof HTMLElement)) return;

        PostSharedTabsSetup.setupPostTabSwitching({
            rootEl,
            onPreview: () => handler.updatePreviewContent?.(),
        });
    }

    public static async initializeMediaManagers(postKey: string, elements: PostEditElements): Promise<void> {
        const normalizedPostId = PostEditSetup.toPostId(postKey);

        if (elements.imageManager && elements.imageManager.dataset.initialized !== "true") {
            try {
                if (normalizedPostId) {
                    ImageSetup.initializeEditMode(normalizedPostId, elements.imageManager, {
                        maxFiles: 10,
                        maxSize: 10 * 1024 * 1024,
                        acceptedTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
                    });
                }
                elements.imageManager.dataset.initialized = "true";
            } catch (error) {
                console.error("[Post Edit Setup] Failed to initialize image handler:", error);
            }
        }

        if (elements.videoManager && elements.videoManager.dataset.initialized !== "true") {
            try {
                if (normalizedPostId) {
                    VideoSetup.initializeEditMode(normalizedPostId, elements.videoManager);
                }
                elements.videoManager.dataset.initialized = "true";
            } catch (error) {
                console.error("[Post Edit Setup] Failed to initialize video manager:", error);
            }
        }
    }

    public static cleanup(postKey: string): void {
        if (PostEditSetup._instances.has(postKey)) {
            PostEditSetup._instances.delete(postKey);
        }
    }
}

export { PostEditSetup };
